/**
 * DraftedDeckModal — post-draft deck review.
 *
 * Two views inside the same dialog:
 *   • list   — one row per card in the drafted deck (sorted by rarity then
 *              name). Click a row to drill into detail.
 *   • detail — full DraftCardChip (printed card front) for the selected
 *              card, with a "Back to list" button.
 *
 * Click anywhere outside the inner panel to close.
 */

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

import { DraftCardChip, draftRarityStyle } from "@/components/draft/DraftCardFront";

export default function DraftedDeckModal({ open, onClose, deck, catalog, totalCards }) {
  const [view, setView] = useState("list");      // "list" | "detail"
  const [selectedId, setSelectedId] = useState(null);

  // Reset to list view whenever the modal re-opens so the user always
  // lands on the overview.
  useEffect(() => {
    if (open) {
      setView("list");
      setSelectedId(null);
    }
  }, [open]);

  if (!open) return null;

  const showCard = (id) => { setSelectedId(id); setView("detail"); };
  const goBack = () => { setSelectedId(null); setView("list"); };

  // Sort: rarity priority (iconic → junk_wax) then alphabetical by name.
  const rarityOrder = ["iconic", "legendary", "exclusive", "rare", "uncommon", "common", "junk_wax"];
  const rarityIndex = (r) => {
    const i = rarityOrder.indexOf(r);
    return i === -1 ? rarityOrder.length : i;
  };
  const rows = (deck || [])
    .map((id, i) => ({ id, key: `${id}-${i}`, card: catalog[id] }))
    .sort((a, b) => {
      const ra = rarityIndex(a.card?.rarity);
      const rb = rarityIndex(b.card?.rarity);
      if (ra !== rb) return ra - rb;
      const na = a.card?.name || a.id;
      const nb = b.card?.name || b.id;
      return na.localeCompare(nb);
    });

  const selectedCard = selectedId ? catalog[selectedId] : null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.18 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] font-black tracking-widest text-amber-400 uppercase">Drafted Deck</p>
            <p className="text-xs text-slate-400">
              {view === "list" ? (() => {
                // totalCards is the server-authoritative target (draft_deck_size).
                // If actual < target the draft ended early (pool ran out etc.);
                // surface that so the count discrepancy isn't a mystery.
                const target = typeof totalCards === "number" ? totalCards : null;
                if (target && rows.length < target) {
                  return `${rows.length} of ${target} cards · draft ended early · tap a card to inspect`;
                }
                return `${rows.length} cards · tap a card to inspect`;
              })() : (() => {
                const idx = rows.findIndex(r => r.id === selectedId);
                const pos = idx >= 0 ? ` · ${idx + 1} / ${rows.length}` : "";
                return `${selectedCard?.name || "Card"}${pos}`;
              })()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-500 hover:text-white transition-colors"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        {view === "list" ? (
          <div className="flex-1 overflow-y-auto px-2 py-2">
            {rows.length === 0 ? (
              <p className="text-xs text-slate-600 italic text-center py-8">No cards in the deck yet.</p>
            ) : (
              <ul className="divide-y divide-slate-800">
                {rows.map(({ id, key, card }) => {
                  const rar = draftRarityStyle(card?.rarity);
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => showCard(id)}
                        className="w-full flex items-center justify-between gap-2 px-2 py-2 hover:bg-slate-800/60 rounded transition-colors text-left"
                      >
                        <span className="text-sm font-bold text-white truncate">
                          {card?.name || id}
                        </span>
                        <span className={`shrink-0 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border ${rar.classes}`}>
                          {rar.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
            <button
              type="button"
              onClick={goBack}
              className="self-start inline-flex items-center gap-1 text-[11px] font-bold text-slate-400 hover:text-amber-400 transition-colors"
            >
              ← Back to list
            </button>
            {(() => {
              const currentIndex = rows.findIndex(r => r.id === selectedId);
              const hasPrev = currentIndex > 0;
              const hasNext = currentIndex < rows.length - 1;
              const navTo = (idx) => { setSelectedId(rows[idx].id); };
              return (
                <div className="relative flex justify-center">
                  <button
                    type="button"
                    onClick={() => hasPrev && navTo(currentIndex - 1)}
                    disabled={!hasPrev}
                    className="absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-xl border border-slate-700 bg-slate-800/60 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className="mx-auto w-full max-w-[260px]">
                    <DraftCardChip card={selectedCard} disabled={true} />
                  </div>
                  <button
                    type="button"
                    onClick={() => hasNext && navTo(currentIndex + 1)}
                    disabled={!hasNext}
                    className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-xl border border-slate-700 bg-slate-800/60 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              );
            })()}
          </div>
        )}
      </motion.div>
    </div>
  );
}
